import * as React from 'react';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { TextArea, TextInput, Text, Tooltip } from '@box/blueprint-web';
import classNames from 'classnames';
import {
  TEXTTYPE_NUMBER,
  TEXTTYPE_EMAIL,
  TEXTTYPE_TEXT,
  MAX_SHORT_TEXT_FIELD_LENGTH,
  MAX_LONG_TEXT_FIELD_LENGTH,
} from '../../../constants';
import isValidDigits from '../../utils/isValidDigits';
import useId from '../../utils/useId';
import css from './blueprint-text-field.module.scss';
import messages from './messages';
import type { Props } from './text-field';
import { BuilderInlineError } from '../../../components/new-file-request-builder/new-form-element/builder-inline-error';

const FLOAT_REGEX = /^[-+]?[0-9]+(\.[0-9]+)?$/;
const isValidNumberValue = (value: string) =>
  (!!value.match(FLOAT_REGEX) && isValidDigits(value)) || value === ''; // Allow to clear the input

const getInputMaxLength = (type: string) => {
  return type === TEXTTYPE_NUMBER ? undefined : MAX_SHORT_TEXT_FIELD_LENGTH;
};

type BlueprintTextFieldProps = Props & {
  intl: IntlShape;
};

const BlueprintTextField = React.memo<BlueprintTextFieldProps>(
  (props: BlueprintTextFieldProps) => {
    const {
      intl,
      description,
      infoText,
      onChange,
      className,
      isDisabled,
      isMultiLine,
      isRequired,
      label,
      error,
      type = TEXTTYPE_TEXT,
      value,
      isSubmitter,
      onBlur,
    } = props;
    let placeholderMessage;
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    let numberInputProps: Record<string, any> = {};
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    const handleOnWheel = (event: any) => {
      // call props.onWheel(), allows for jest mock functions to be passed in for testing.
      if (props.onWheel) {
        props.onWheel();
      }

      // Prevent the input value change
      event.target.blur();
      // Refocus immediately, on the next tick (after the current function is done)
      setTimeout(() => {
        event.target.focus();
      }, 0);
    };
    if (isMultiLine) {
      placeholderMessage = messages.multilineTextFieldPlaceholderMessage;
    } else if (type === TEXTTYPE_EMAIL) {
      placeholderMessage = messages.emailTextFieldPlaceholderMessage;
    } else if (type === TEXTTYPE_NUMBER) {
      placeholderMessage = messages.numberTextFieldPlaceholderMessage;
      numberInputProps = {
        onWheel: handleOnWheel,
      };
    } else {
      placeholderMessage = messages.textFieldPlaceholderMessage;
    }
    const placeholder = intl.formatMessage(placeholderMessage);

    const descriptionID = useId('description');
    const labelID = useId('label');
    const ariaAttrs = {
      'aria-describedby': description ? descriptionID : undefined,
      'aria-labelledby': labelID,
    } as const;

    const inputMaxLength = getInputMaxLength(type);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (type === TEXTTYPE_NUMBER && !isValidNumberValue(newValue)) {
        return;
      }

      onChange(newValue);
    };

    return (
      <div className={classNames(className, css.textFieldContainer)}>
        <Text
          id={labelID}
          as="label"
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2820
          color="text-on-light-default"
          variant="subtitle"
          className={classNames({ [css.label]: isSubmitter })}
        >
          {label}
        </Text>
        <Tooltip
          content={infoText}
          // Hide tooltip when no info text provided
          open={infoText ? undefined : false}
          side="right"
          variant="standard"
        >
          {isMultiLine ? (
            <TextArea
              className={css.textareaField}
              data-testid="blueprint-textarea"
              disabled={isDisabled}
              error={error}
              maxLength={MAX_LONG_TEXT_FIELD_LENGTH}
              minRows={3}
              maxRows={5}
              // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
              onChange={handleOnChange}
              onBlur={onBlur}
              placeholder={placeholder}
              required={isRequired}
              value={value}
              {...ariaAttrs}
            />
          ) : (
            <TextInput
              className={css.textInputField}
              data-testid="blueprint-textinput"
              disabled={isDisabled}
              error={error}
              maxLength={inputMaxLength}
              onChange={handleOnChange}
              onBlur={onBlur}
              placeholder={placeholder}
              required={isRequired}
              // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
              type={type}
              value={value}
              {...ariaAttrs}
              {...numberInputProps}
            />
          )}
        </Tooltip>

        {description && (
          <Text
            as="span"
            className={css.descriptionText}
            // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
            color="textOnLight"
            id={descriptionID}
            variant="bodyDefault"
          >
            {description}
          </Text>
        )}

        <BuilderInlineError />
      </div>
    );
  },
);

export default injectIntl(BlueprintTextField);
